// The Exchange on the client (rules: shared/exchange.ts): the sell-to-the-Exchange dialog (the
// Market's Sell tab and item pages offer it on items the Exchange is buying today) and its Admin
// section. The server only sends { exchange } while it is on.
import type { ClientMsg } from '../shared/protocol.ts';
import { EXCHANGE, EXCHANGE_NAME, type ExchangeAdmin, type ExchangeDTO, type ExchangeEdit } from '../shared/exchange.ts';
import { ITEM } from '../shared/items.ts';
import { CONFIG } from '../shared/config.ts';
import { marketFee } from '../shared/market.ts';
import { S } from './state.ts';
import { esc, full } from './fmt.ts';
import { openDialog } from './dialog.ts';

let send: (m: ClientMsg) => void = () => {};
export function bindExchange(o: { send: typeof send }) {
  send = o.send;
  // Admin drafts survive re-renders until saved.
  document.addEventListener('input', (e) => {
    const el = e.target as HTMLInputElement;
    const k = el.dataset?.exf;
    if (k) draft.set(k, el.type === 'checkbox' ? String(el.checked) : el.value);
  });
}

const dto = (): ExchangeDTO | undefined => (S.market as { exchange?: ExchangeDTO } | null)?.exchange;

/** Units of an item the player may trade (admin-granted ones never can). */
function tradable(id: string): number {
  const t = S.trainer;
  return t ? Math.max(0, (t.bag[id] ?? 0) - (t.devItems?.[id] ?? 0)) : 0;
}

async function sellDialog(id: string) {
  const o = dto()?.offers.find((x) => x.id === id);
  if (!o) return;
  const max = Math.min(o.mineLeft, tradable(id));
  if (max < 1) return;
  const name = ITEM[id]?.name ?? id;
  const net = (q: number) => q * o.bid - marketFee(q * o.bid);
  const r = await openDialog({
    title: `Sell ${name} to ${EXCHANGE_NAME}`,
    body: `<label class="dlg-field"><span>Quantity</span><input name="qty" type="number" min="1" max="${max}" value="${max}" inputmode="numeric"> <span class="sub">of ${full(max)}</span></label>
      <p class="sub">${full(o.bid)} coins each, less the ${Math.round(CONFIG.marketFeeCoins * 100)}% market fee</p>`,
    actions: [{ label: `Sell for ${full(net(max))} coins`, value: 'sell', kind: 'primary' }],
    onInput: (root, f) => {
      const q = Math.floor(Number(f.qty) || 0), ok = q >= 1 && q <= max;
      const b = [...root.querySelectorAll<HTMLButtonElement>('.actions button')].pop()!;
      b.disabled = !ok;
      b.textContent = ok ? `Sell ${full(q)} for ${full(net(q))} coins` : 'Sell';
    },
  });
  const q = Math.floor(Number(r.data.qty));
  if (r.value && q >= 1 && q <= max) send({ t: 'exchangeSell', item: id, qty: q });
}

// ---------- Admin ----------
const draft = new Map<string, string>();
const dv = (id: string, f: string, v: number | boolean) => draft.get(`${id}:${f}`) ?? String(v);
const numIn = (id: string, f: string, v: number, w = 110) => `<input type="number" min="0" data-exf="${esc(id)}:${f}" value="${esc(dv(id, f, v))}" aria-label="${f}" style="width:${w}px">`;
const boxIn = (id: string, f: string, v: boolean, label: string) => `<label class="row"><input type="checkbox" data-exf="${esc(id)}:${f}"${dv(id, f, v) === 'true' ? ' checked' : ''}> ${label}</label>`;

export function exchangeAdminSection(a: ExchangeAdmin | undefined): string {
  if (!a) return '';
  const rows = a.items.map((i) => {
    const name = ITEM[i.id]?.name ?? i.id;
    const stopped = i.stage >= EXCHANGE.taperStages;
    return `<tr><td>${esc(name)}${stopped ? ' <span class="muted">(tapered off)</span>' : ''}</td>
      <td>${boxIn(i.id, 'on', i.on, 'On')}${boxIn(i.id, 'auto', i.auto, 'Auto')}</td>
      <td>${numIn(i.id, 'ref', i.ref)}<div class="muted">bid ${full(i.bid)} · ask ${full(i.ask)}</div></td>
      <td>${numIn(i.id, 'stock', i.stock, 70)} / ${numIn(i.id, 'budget', i.budget, 70)}<div class="muted">now ${full(i.caps.stock)} / ${full(i.caps.budget)}</div>
        <div class="muted">gate ${numIn(i.id, 'gate', i.gate ?? 0, 60)}${i.gate ? (i.open ? ' open' : ' shut') : ''}</div></td>
      <td>${full(i.today.sold)} / ${full(i.today.bought)}${i.inv ? `<div class="muted">holds ${full(i.inv)}</div>` : ''}</td>
      <td>${full(i.player7)} vs ${full(i.exchange7)}${i.playerAvg7 !== null ? `<div class="muted">players avg ${full(i.playerAvg7)}</div>` : ''}</td>
      <td>${numIn(i.id, 'stage', i.stage, 50)}</td>
      <td><button class="btn small" data-act="ex-save" data-id="${esc(i.id)}">Save</button></td></tr>`;
  }).join('');
  const top = a.top.map((t) => `<tr><td>${esc(t.who)}</td><td>${full(t.trades)}</td><td>${full(t.coins)}</td></tr>`).join('');
  return `<div class="row between" style="margin-top:16px"><h3>${esc(EXCHANGE_NAME)}</h3><span class="muted">${a.retired ? 'Retired: players stock the market (Cole 2026-10-20)' : a.on ? (a.share > 0 ? 'On' : 'Closed') : 'Off'}${a.share > 0 && a.share < 1 ? ` · fading, ${Math.round(a.share * 100)}% today` : ''}${a.closesAt ? ` · ${a.share > 0 ? 'closes' : 'closed'} ${new Date(a.closesAt).toISOString().slice(0, 10)}` : ' · no launch day set'} · restocks ${new Date(a.resetAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
      <button class="btn small" data-act="ex-power">${a.on ? 'Turn off (ends its listings)' : 'Turn on'}</button></div>
    <p class="hint">Coins only. Last 7 days: paid players ${full(a.minted7)} coins (minted), took ${full(a.burned7)} (burned). Demand pricing moves a reference at most ${Math.round(EXCHANGE.maxDayMove * 100)}% a day; it tapers an item when players trade ${EXCHANGE.taperRatio}× its volume for a week (stage ${EXCHANGE.taperStages} = stopped). An item with a gate gets no daily stock until players sell that many among themselves in 7 days (0 = no gate); it still buys and resells. It runs ${EXCHANGE.launchDays} days from launch day, then fades out over ${EXCHANGE.fadeDays} and closes.</p>
    <table class="tbl"><tr><th>Item</th><th></th><th>Reference</th><th>Stock / budget a day</th><th>Today sold / bought</th><th>7 d players vs Exchange</th><th>Stage</th><th></th></tr>${rows || '<tr><td colspan=8>Nothing carried</td></tr>'}</table>
    ${top ? `<h4 class="adm-h">Top traders with it (7 d)</h4><table class="tbl"><tr><th>Player</th><th>Trades</th><th>Coins</th></tr>${top}</table>` : ''}`;
}

/** Clicks for data-act="ex-*"; true when handled. */
export function exchangeClick(el: HTMLElement): boolean {
  const act = el.dataset.act;
  if (act === 'ex-sell') { void sellDialog(el.dataset.id ?? ''); return true; }
  if (act === 'ex-power') { send({ t: 'admExchange', on: !(S.admin as { exchange?: ExchangeAdmin } | null)?.exchange?.on }); return true; }
  if (act === 'ex-save') {
    const id = el.dataset.id ?? '';
    const row = el.closest('tr');
    const set: ExchangeEdit = {};
    for (const inp of row?.querySelectorAll<HTMLInputElement>('[data-exf]') ?? []) {
      const f = inp.dataset.exf!.split(':')[1] as keyof ExchangeEdit;
      if (inp.type === 'checkbox') (set as Record<string, unknown>)[f] = inp.checked;
      else (set as Record<string, unknown>)[f] = Math.floor(Number(inp.value));
      draft.delete(inp.dataset.exf!);
    }
    send({ t: 'admExchange', item: id, set });
    return true;
  }
  return false;
}
